import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import NotifyCustomersLink from "../components/NotifyCustomersLink";
import ProviderLeave from "../components/ProviderLeave";
import ProviderModal from "../components/ProviderModal";
import ProviderSchedule from "../components/ProviderSchedule";
import Toast from "../components/Toast";
import useUrlState, { param } from "../hooks/useUrlState";
import { apiErrorMessage } from "../services/api";
import { fetchBranches } from "../services/branchService";
import {
  deleteProviderPhoto,
  fetchProvider,
  uploadProviderPhoto,
} from "../services/providerService";
import { fetchCatalogue } from "../services/serviceService";
import { labelFor } from "../services/enumLabels";

const TABS = ["profile", "schedule", "leave"];

/**
 * BIZ-08 — one provider, with their schedule (BIZ-09) and leave (BIZ-10)
 * alongside. All three read the same provider, so they live on one page rather
 * than three routes the user has to navigate between.
 */
export default function ProviderProfile() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();

  const [provider, setProvider] = useState(null);
  const [branches, setBranches] = useState([]);
  const [services, setServices] = useState([]);
  // The open tab lives in the URL (`?tab=`): Back returns to it, and a
  // schedule notification lands on "schedule".
  const { values: tabState, set: setTabState } = useUrlState(
    { tab: param.oneOf(TABS, "profile") },
    { pageKey: null }
  );
  const { tab } = tabState;
  const setTab = useCallback((next) => setTabState({ tab: next }), [setTabState]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const fileInput = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setProvider(await fetchProvider(id));
    } catch (err) {
      setError(apiErrorMessage(err, t("providers.loadOneFailed")));
      setProvider(null);
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    load();
  }, [load, i18n.language]);

  useEffect(() => {
    fetchBranches(1, 100)
      .then(({ branches: list }) => setBranches(list))
      .catch(() => setBranches([]));

    fetchCatalogue({ perPage: 100 })
      .then(({ services: list }) => setServices(list))
      .catch(() => setServices([]));
  }, [i18n.language]);

  const onPhotoPicked = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPhotoBusy(true);
    try {
      setProvider(await uploadProviderPhoto(id, file));
      setToast({ type: "success", message: t("providers.photo.uploaded") });
    } catch (err) {
      setToast({ type: "error", message: apiErrorMessage(err, t("providers.photo.failed")) });
    } finally {
      setPhotoBusy(false);
      // Let the same file be chosen again after a failure.
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const removePhoto = async () => {
    setPhotoBusy(true);
    try {
      await deleteProviderPhoto(id);
      await load();
      setToast({ type: "success", message: t("providers.photo.removed") });
    } catch (err) {
      setToast({ type: "error", message: apiErrorMessage(err, t("providers.photo.failed")) });
    } finally {
      setPhotoBusy(false);
    }
  };

  if (loading) {
    return <p className="py-16 text-center text-sm text-ink-2">{t("common.loading")}</p>;
  }

  if (error || !provider) {
    return (
      <div>
        <Link to="/providers" className="text-sm font-medium text-accent-text hover:underline">
          ← {t("providers.backToList")}
        </Link>
        <div className="mt-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {error || t("providers.notFound")}
        </div>
      </div>
    );
  }

  // The schedule editor only ever offers branches this provider actually works
  // at — the API refuses the rest anyway.
  const providerBranches = provider.branches ?? [];
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div>
      <Link to="/providers" className="text-sm font-medium text-accent-text hover:underline">
        ← {t("providers.backToList")}
      </Link>

      {/* Header */}
      <div className="card mt-3 mb-4 flex flex-wrap items-center justify-between gap-4 p-5">
        <div className="flex items-center gap-4">
          {provider.photo_url ? (
            <img
              src={provider.photo_url}
              alt=""
              className="h-16 w-16 rounded-full object-cover"
            />
          ) : (
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-raised text-xl font-medium text-ink-2">
              {(provider.display_name ?? "?").slice(0, 1)}
            </span>
          )}

          <div>
            <h1 className="text-xl font-bold">{provider.display_name}</h1>
            {provider.job_title && <p className="text-sm text-ink-2">{provider.job_title}</p>}
            <div className="mt-1 flex flex-wrap gap-2">
              <span className="rounded-full bg-raised px-2 py-1 text-xs text-ink-2">
                {labelFor("employment_status", provider.employment_status)}
              </span>
              {provider.user && (
                <span className="rounded-full bg-accent-wash px-2 py-1 text-xs text-accent-text">
                  {t("providers.hasLogin", { email: provider.user.email })}
                </span>
              )}
            </div>
            {/* Tell this provider's affected customers (needs notices.send). */}
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
              <NotifyCustomersLink
                type="staff_running_late"
                scope={{ staff_id: provider.id, delay_minutes: 15 }}
                label={t("notices.shortcuts.runningLate")}
              />
              <NotifyCustomersLink
                type="staff_urgent_leave"
                scope={{ staff_id: provider.id, date_from: today, date_to: today }}
                label={t("notices.shortcuts.urgentLeave")}
              />
              {providerBranches.length > 0 && (
                <NotifyCustomersLink
                  type="staff_moved_branch"
                  scope={{ staff_id: provider.id, from_branch_id: providerBranches[0].id, date_from: today }}
                  label={t("notices.shortcuts.movedBranch")}
                />
              )}
              <NotifyCustomersLink
                type="staff_left"
                scope={{ staff_id: provider.id, date_from: today }}
                label={t("notices.shortcuts.providerLeft")}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <input
            ref={fileInput}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={onPhotoPicked}
          />
          <button
            type="button"
            className="btn-secondary"
            disabled={photoBusy}
            onClick={() => fileInput.current?.click()}
          >
            {provider.photo_url ? t("providers.photo.replace") : t("providers.photo.upload")}
          </button>
          {provider.photo_url && (
            <button
              type="button"
              className="btn-secondary"
              disabled={photoBusy}
              onClick={removePhoto}
            >
              {t("providers.photo.remove")}
            </button>
          )}
          <button type="button" className="btn-primary" onClick={() => setEditOpen(true)}>
            {t("common.edit")}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-4 flex gap-1 border-b border-line">
        {TABS.map((name) => (
          <button
            key={name}
            type="button"
            onClick={() => setTab(name)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
              tab === name
                ? "border-accent text-accent-text"
                : "border-transparent text-ink-2 hover:text-ink"
            }`}
          >
            {t(`providers.tabs.${name}`)}
          </button>
        ))}
      </div>

      {tab === "profile" && (
        <div className="card space-y-4 p-5">
          {provider.bio && (
            <div>
              <p className="field-label">{t("providers.form.bio")}</p>
              <p className="text-sm text-ink">{provider.bio}</p>
            </div>
          )}

          <div>
            <p className="field-label">{t("providers.form.branches")}</p>
            {providerBranches.length ? (
              <div className="flex flex-wrap gap-2">
                {providerBranches.map((branch) => (
                  <span
                    key={branch.id}
                    className="rounded-full bg-raised px-3 py-1 text-sm text-ink"
                  >
                    {branch.name}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-ink-2">{t("providers.noBranches")}</p>
            )}
          </div>

          <div>
            <p className="field-label">{t("providers.form.services")}</p>
            {provider.services?.length ? (
              <div className="flex flex-wrap gap-2">
                {provider.services.map((service) => (
                  <span
                    key={service.id}
                    className="rounded-full bg-raised px-3 py-1 text-sm text-ink"
                  >
                    {service.name}
                    <span className="ms-1 text-xs text-ink-2">
                      {t("providers.minutes", { count: service.duration })}
                    </span>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-ink-2">{t("providers.noServices")}</p>
            )}
          </div>

          {/* Present only when the caller may see it — the API omits the key. */}
          {provider.commission_type !== undefined && (
            <div>
              <p className="field-label">{t("providers.form.commission")}</p>
              <p className="text-sm text-ink">
                {provider.commission_type === "none"
                  ? t("providers.commissionTypes.none")
                  : labelFor("commission_type", provider.commission_type) +
                    " — " +
                    provider.commission_value}
              </p>
            </div>
          )}
        </div>
      )}

      {tab === "schedule" && (
        <ProviderSchedule providerId={provider.id} branches={providerBranches} />
      )}

      {tab === "leave" && <ProviderLeave providerId={provider.id} />}

      <ProviderModal
        provider={provider}
        branches={branches}
        services={services}
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSaved={(saved) => {
          setProvider(saved);
          setToast({ type: "success", message: t("providers.updated", { name: saved.display_name }) });
        }}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
